import { useContext, useEffect, useRef, useState } from 'react';
import fileDownload from 'js-file-download';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardHeader,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { grey } from '@mui/material/colors';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import TranslateIcon from '@mui/icons-material/Translate';
import useFileService, {
  VttTranslationReport,
} from '../../../hooks/useFileService';
import DropZone from '../../Report/Input/Dropzone/Dropzone';
import { IFileInfo, Severity } from '../../../types/reports';
import { SocketContext } from '../../../context/SocketProvider';
import { ISocketContext } from '../../../types/socketclient';
import returnDate from '../../../utils/dateTime';
import { ProgressBar } from '../../../global';
import DebugWindow from '../../Report/DebugWindow/DebugWindow';
import SUPPORTED_LANGUAGES, { Language } from './languages';

interface TranslationProgress {
  language: string;
  langIndex: number;
  totalLangs: number;
  batch: number;
  totalBatches: number;
  status: string;
}

const VttTranslation = () => {
  const theme = useTheme();
  const [uploadFile, progressUpdate, downloadFile, getFiles, deleteFile] =
    useFileService();
  const { socket } = useContext(SocketContext) as ISocketContext;

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const clearDropzoneRef = useRef<(() => void) | null>(null);
  const [fileInfos, setFileInfos] = useState<IFileInfo[]>([]);
  const [selectedLanguages, setSelectedLanguages] = useState<Language[]>([]);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<Severity>('warning');
  const [pythonOutput, setPythonOutput] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [translationProgress, setTranslationProgress] =
    useState<TranslationProgress | null>(null);

  const isReady =
    selectedFiles.length > 0 && selectedLanguages.length > 0 && !isUploading;

  useEffect(() => {
    socket?.on('pythonOutput', (output: string) => {
      setPythonOutput((prev) => [...prev, output]);
    });
    socket?.on('translationProgress', (progress: TranslationProgress) => {
      setTranslationProgress(progress);
    });
    socket?.on('uploadComplete', (file) => {
      setFileInfos((prev) => {
        const filtered = prev.filter((f) => f.url !== file.url);
        return [...filtered, file];
      });
    });
    socket?.on('deleteFile', (fileName: string) => {
      setFileInfos((prev) => prev.filter((f) => f.name !== fileName));
    });
    return () => {
      socket?.off('pythonOutput');
      socket?.off('translationProgress');
      socket?.off('uploadComplete');
      socket?.off('deleteFile');
    };
  }, [socket]);

  useEffect(() => {
    const getUploadedFiles = async () => {
      const result = await getFiles();
      if (result.success) {
        setFileInfos(result.data as unknown as IFileInfo[]);
      }
    };
    getUploadedFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileChange = (files: File[]) => {
    setSelectedFiles(files);
  };

  const clearFiles = () => {
    if (clearDropzoneRef.current) {
      clearDropzoneRef.current();
    }
  };

  const upload = async () => {
    if (!isReady) return;

    setIsUploading(true);
    setTranslationProgress(null);
    setPythonOutput([]);
    setSeverity('info');
    setMessage('Translating...');

    const reportInput: VttTranslationReport = {
      files: selectedFiles,
      languages: selectedLanguages.map((l) => l.code),
    };

    const result = await uploadFile(reportInput);

    if (result.success) {
      setMessage(result.message || 'Translation complete!');
      setSeverity('success');
    } else {
      setMessage(result.error || 'Translation failed.');
      setSeverity('error');
    }

    setSelectedFiles([]);
    clearFiles();
    setIsUploading(false);
    setTranslationProgress(null);
  };

  const isZip = (name: string) => name.endsWith('.zip');

  const batchPercent = translationProgress
    ? Math.round(
        (translationProgress.batch / translationProgress.totalBatches) * 100,
      )
    : 0;

  const overallPercent = translationProgress
    ? Math.round(
        ((translationProgress.langIndex -
          1 +
          translationProgress.batch / translationProgress.totalBatches) /
          translationProgress.totalLangs) *
          100,
      )
    : 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Upload Progress Bar */}
      {progressUpdate > 0 && (
        <Box sx={{ width: '100%' }}>
          <ProgressBar value={progressUpdate} />
        </Box>
      )}

      {/* Dropzone */}
      <DropZone
        selectedFiles={selectedFiles}
        onFileChange={handleFileChange}
        clearFiles={(clearFunc: () => void) => {
          clearDropzoneRef.current = clearFunc;
        }}
      />

      {/* Language Selector */}
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TranslateIcon fontSize="small" />
              <Typography variant="subtitle1" fontWeight={600}>
                Target Languages
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ ml: 'auto' }}
              >
                {selectedLanguages.length} selected
              </Typography>
            </Box>
          }
          sx={{
            bgcolor: grey[50],
            borderBottom: `1px solid ${grey[200]}`,
            py: 1,
          }}
        />
        <Box sx={{ p: 2 }}>
          <Autocomplete
            multiple
            options={SUPPORTED_LANGUAGES}
            value={selectedLanguages}
            onChange={(_event, newValue) => setSelectedLanguages(newValue)}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            filterOptions={(options, { inputValue }) => {
              const query = inputValue.toLowerCase();
              if (!query) return options;
              return options.filter(
                (opt) =>
                  opt.name.toLowerCase().includes(query) ||
                  opt.code.toLowerCase().includes(query),
              );
            }}
            renderTags={(value, getTagProps) =>
              value.map((lang, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={lang.code}
                  label={lang.name}
                  size="small"
                  color="primary"
                  sx={{
                    fontWeight: 500,
                    borderRadius: '16px',
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={
                  selectedLanguages.length === 0
                    ? 'Search and select languages...'
                    : 'Add more languages...'
                }
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            )}
            sx={{
              '& .MuiAutocomplete-tag': {
                my: '2px',
              },
            }}
            disableCloseOnSelect
            noOptionsText="No matching languages"
          />
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button
              size="small"
              variant="text"
              onClick={() => setSelectedLanguages([...SUPPORTED_LANGUAGES])}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              Select All ({SUPPORTED_LANGUAGES.length})
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => setSelectedLanguages([])}
              disabled={selectedLanguages.length === 0}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              Clear All
            </Button>
          </Box>
        </Box>
      </Card>

      {/* Translation Progress */}
      {isUploading && translationProgress && (
        <Card
          variant="outlined"
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CircularProgress size={20} thickness={5} />
              <Typography variant="subtitle2" fontWeight={600}>
                Translating to {translationProgress.language}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ ml: 'auto' }}
              >
                Language {translationProgress.langIndex} of{' '}
                {translationProgress.totalLangs}
              </Typography>
            </Box>

            {/* Current language progress */}
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: '4px',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Batch {translationProgress.batch} of{' '}
                  {translationProgress.totalBatches}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {batchPercent}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={batchPercent}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>

            {/* Overall progress */}
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: '4px',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Overall progress
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {overallPercent}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={overallPercent}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: grey[200],
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                  },
                }}
              />
            </Box>

            {/* Status line */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 1 }}
            >
              {translationProgress.status}
            </Typography>
          </Box>
        </Card>
      )}

      {/* Debug Window */}
      <DebugWindow data={pythonOutput} />

      {/* Upload Button */}
      <Box sx={{ textAlign: 'center' }}>
        <Button
          variant="contained"
          size="large"
          disabled={!isReady}
          onClick={upload}
          startIcon={<TranslateIcon />}
          sx={{
            px: 5,
            py: 1,
            borderRadius: 2,
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '1rem',
          }}
        >
          {isUploading ? 'Translating...' : 'Translate'}
        </Button>
      </Box>

      {/* Alert */}
      {message.length > 0 && (
        <Stack spacing={2}>
          <Alert severity={severity} onClose={() => setMessage('')}>
            {message}
          </Alert>
        </Stack>
      )}

      {/* File List */}
      {fileInfos && fileInfos.length > 0 && (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardHeader
            title={
              <Typography variant="subtitle1" fontWeight={600}>
                Translated Files
              </Typography>
            }
            sx={{
              bgcolor: grey[50],
              borderBottom: `1px solid ${grey[200]}`,
              py: 1,
            }}
          />
          <List disablePadding>
            {fileInfos.map((file, idx) => (
              <ListItem
                key={`${file.name}-${file.date}`}
                divider={idx < fileInfos.length - 1}
                sx={{
                  justifyContent: 'space-between',
                  py: 1,
                  px: 2,
                  ...(isZip(file.name) && {
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                  }),
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    minWidth: 0,
                  }}
                >
                  {isZip(file.name) ? (
                    <FolderZipIcon color="primary" fontSize="small" />
                  ) : (
                    <TranslateIcon color="action" fontSize="small" />
                  )}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={isZip(file.name) ? 600 : 400}
                      noWrap
                    >
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {returnDate(file.date)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                  <Button
                    variant={isZip(file.name) ? 'contained' : 'outlined'}
                    startIcon={<FileDownloadIcon />}
                    size="small"
                    sx={{ textTransform: 'none', borderRadius: 1 }}
                    onClick={async () => {
                      const result = await downloadFile(file.name);
                      if (result.success) {
                        fileDownload(result.data as unknown as File, file.name);
                      }
                    }}
                  >
                    {isZip(file.name) ? 'Download All' : 'Download'}
                  </Button>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={async () => {
                      await deleteFile(file.name);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </ListItem>
            ))}
          </List>
        </Card>
      )}
    </Box>
  );
};

export default VttTranslation;
